"use client";

// MKT-#10: gate that decides WHEN to load the aws-amplify cluster.
// - Portal / auth / catalog / everything not in the content-marketing
//   allowlist: mount immediately (Amplify eager, behavior unchanged - the
//   dynamic chunk is fetched right away and configures on hydration, before
//   any user interaction that needs auth).
// - Public content-marketing routes (home, /about, metros, cornerstone, ...):
//   mount AFTER first paint - on browser idle OR the first user interaction,
//   whichever comes first - so the Amplify bundle no longer blocks the hero
//   LCP paint. A signed-in visitor's nav state does NOT depend on this: it
//   comes from the mag-portal-access cookie (see useIsSignedIn/useIsApproved).
//
// ssr:false is correct: the cluster renders nothing visible (config, cookie
// mirror, hidden cart drawer, toasts), so there is no flash / layout shift.

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { shouldDeferAmplify } from "@/lib/marketing/amplify-defer";

const AmplifyCluster = dynamic(
  () => import("@/components/auth/AmplifyCluster").then((m) => m.AmplifyCluster),
  { ssr: false },
);

export function AmplifyRuntime() {
  const pathname = usePathname();
  const defer = shouldDeferAmplify(pathname);
  const [mounted, setMounted] = useState(!defer);

  useEffect(() => {
    if (!defer || mounted) return;
    let done = false;
    const mount = () => {
      if (done) return;
      done = true;
      setMounted(true);
    };
    // Whichever fires first: browser idle, a short fallback, or the first
    // user interaction (covers e.g. a signed-in user clicking sign-out).
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(mount, { timeout: 2500 });
    } else {
      window.setTimeout(mount, 1500);
    }
    const opts = { once: true, passive: true } as const;
    window.addEventListener("pointerdown", mount, opts);
    window.addEventListener("keydown", mount, opts);
    return () => {
      window.removeEventListener("pointerdown", mount);
      window.removeEventListener("keydown", mount);
    };
  }, [defer, mounted]);

  return mounted ? <AmplifyCluster /> : null;
}
